/**
 * VÉRAULT Portal — Client: Trip Home (LIGHTHOUSE)
 *
 * The first thing a client sees of their trip. Editorial luxury reading room —
 * full-bleed hero photograph, serif title, calm cards for dates, hotel, flights,
 * and a quiet concierge whisper.  Reveal-mode aware.
 */
import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  CalendarDays,
  Hotel,
  Plane,
  MapPin,
  MessageCircle,
  Sparkles,
  ScrollText,
  CheckCircle2,
  ClipboardList,
  ListChecks,
} from "lucide-react";
import {
  Surface,
  Heading,
  EditorialRule,
  KeyValue,
  PortalButton,
  StatusTag,
  AIWhisper,
  Empty,
  Skeleton,
  PortalBreadcrumb,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";
import { ClientDestinationsView } from "./ClientDestinationsView";
import { usePreviewBase } from "../../contexts/PreviewContext";

const { fadeInUp, stagger, fadeIn } = portalMotion;

// Wave 26.15 — Pre-trip readiness teaser on the client trip home.
// Only renders when the advisor has revealed the readiness brief.
function ReadinessTeaser({ tripId }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    api
      .get(`/client-portal/trips/${tripId}/readiness`)
      .then(({ data }) => alive && setData(data?.readiness ? data : null))
      .catch(() => alive && setData(null));
    return () => { alive = false; };
  }, [tripId]);
  if (!data?.readiness) return null;
  const personal = data.readiness.personal_checklist?.items || [];
  const docs = data.readiness.document_checklist?.items || [];
  const total = personal.length;
  const done = personal.filter((it) => it.done).length;
  const blockingDocs = docs.filter((it) => it.status === "blocking" || it.status === "missing").length;
  return (
    <motion.div variants={fadeInUp}>
      <Link
        to={`/portal/client/trip/${tripId}/readiness`}
        className="block p-5 rounded-md border border-[hsl(var(--accent-gold))]/25 bg-gradient-to-br from-[hsl(var(--accent-gold))]/[0.06] to-transparent hover:border-[hsl(var(--accent-gold))]/45 transition-colors group"
        data-testid="trip-readiness-teaser"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <span className="inline-flex items-center justify-center size-10 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] shrink-0">
              <ListChecks className="size-5" strokeWidth={1.5} />
            </span>
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                Before you go
              </p>
              <p className="mt-1 font-serif text-[18px] text-[hsl(var(--foreground))]">
                Your pre-trip checklist
              </p>
              <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
                {total > 0
                  ? `${done} of ${total} done` + (blockingDocs > 0 ? ` · ${blockingDocs} need${blockingDocs === 1 ? "s" : ""} your attention` : "")
                  : "A few quiet reminders ahead of your trip."}
              </p>
            </div>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))] group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1 self-end sm:self-center">
            Open →
          </span>
        </div>
      </Link>
    </motion.div>
  );
}


// --- Reveal-mode mask helpers --------------------------------------
function masked(value, mask, mode) {
  if (mode === "masked" || mode === "hidden") {
    return mask || "Coming soon";
  }
  return value;
}

// --- Hero -----------------------------------------------------------
function TripHero({ trip }) {
  const r = trip.reveal || {};
  const destMode = r.destination || trip.reveal_destination || "full";
  const datesMode = r.dates || trip.reveal_dates || "full";
  const destMask = r.destination_mask || trip.reveal_destination_mask || "A surprise ✨";
  const datesMask = r.dates_mask || trip.reveal_dates_mask || "Coming soon";

  const title = destMode === "full"
    ? (trip.client_trip_title || trip.destinations || "Your experience")
    : destMask;

  const dateLabel = datesMode === "full"
    ? fmt.formatDateRange(trip.trip_start_date || trip.start_date, trip.trip_end_date || trip.end_date)
    : datesMode === "partial"
      ? `${fmt.formatDate(trip.trip_start_date || trip.start_date, { month: "long", year: "numeric" })}`
      : datesMask;

  const hero = trip.hero_image_url;
  const days = fmt.daysUntil(trip.trip_start_date || trip.start_date);

  return (
    <motion.section
      variants={fadeIn}
      initial="initial"
      animate="animate"
      className="relative overflow-hidden rounded-[var(--radius)] border border-[rgba(0,0,0,0.06)] shadow-soft-lift"
      data-testid="trip-hero"
    >
      <div className="aspect-[16/9] sm:aspect-[21/9] lg:aspect-[21/8] relative bg-[hsl(var(--background-stone))]">
        {hero ? (
          <img
            src={hero}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--background-paper))] via-[hsl(var(--background-stone))] to-[hsl(var(--background-paper))]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10 lg:p-14 text-white">
          <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-white/80">
            {trip.client_trip_subline || "Your Experience"}
          </p>
          <h1 className="mt-2 font-serif text-[34px] sm:text-[48px] lg:text-[64px] leading-[1.05] tracking-[-0.02em] max-w-3xl">
            {title}
          </h1>
          <p className="mt-3 font-accent italic text-[15px] sm:text-lg text-white/90">
            {dateLabel}
            {days != null && datesMode === "full" && days > 0 ? ` · ${days} day${days === 1 ? "" : "s"} away` : ""}
            {days != null && days === 0 ? " · today" : ""}
          </p>
        </div>
      </div>
    </motion.section>
  );
}

// --- Concierge whisper ---------------------------------------------
function ClientConcierge({ trip }) {
  // Surface trip.welcome_message or intro_note as a hand-written welcome.
  const note = trip.welcome_message || trip.intro_note || trip.journey_overview;
  if (!note) return null;
  return (
    <motion.div variants={fadeInUp}>
      <AIWhisper label="A note from your advisor" testid="trip-welcome-note">
        {note}
      </AIWhisper>
    </motion.div>
  );
}

// --- Logistics row ------------------------------------------------
function Logistics({ trip }) {
  const r = trip.reveal || {};
  const iMode = r.itinerary || trip.reveal_itinerary || "full";

  const hotel = trip.hotel || {};
  const arrival = trip.arrival || {};
  const dep = trip.departure || {};

  const hotelName = hotel.name || trip.hotel_name;
  const arrFlight = arrival.flight_number || trip.arrival_flight_number;
  const depFlight = dep.flight_number || trip.departure_flight_number;

  if (iMode === "hidden") {
    return (
      <Surface variant="paper">
        <div className="text-center py-6">
          <Sparkles className="size-5 mx-auto text-[hsl(var(--accent-gold))] mb-3" strokeWidth={1.25} />
          <p className="font-accent italic text-base text-[hsl(var(--foreground-secondary))]">
            Your itinerary is being arranged. The reveal is coming soon.
          </p>
        </div>
      </Surface>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
      <Surface variant="default" padded className="!p-5" data-testid="logistics-dates">
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
          <Heading level="eyebrow">Dates</Heading>
        </div>
        <p className="font-serif text-lg text-[hsl(var(--foreground))]">
          {fmt.formatDateRange(trip.trip_start_date || trip.start_date, trip.trip_end_date || trip.end_date) || "TBD"}
        </p>
        {trip.num_travelers && (
          <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))]">
            {trip.num_travelers} traveler{trip.num_travelers === 1 ? "" : "s"}
          </p>
        )}
      </Surface>

      <Surface variant="default" padded className="!p-5" data-testid="logistics-hotel">
        <div className="flex items-center gap-2 mb-3">
          <Hotel className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
          <Heading level="eyebrow">Stay</Heading>
        </div>
        {hotelName ? (
          <>
            <p className="font-serif text-lg text-[hsl(var(--foreground))]">{hotelName}</p>
            {(hotel.address || trip.hotel_address) && (
              <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))]">
                {hotel.address || trip.hotel_address}
              </p>
            )}
          </>
        ) : (
          <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            Your home base is being finalised.
          </p>
        )}
      </Surface>

      <Surface variant="default" padded className="!p-5" data-testid="logistics-flights">
        <div className="flex items-center gap-2 mb-3">
          <Plane className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
          <Heading level="eyebrow">Flights</Heading>
        </div>
        {arrFlight || depFlight ? (
          <div className="space-y-2">
            {arrFlight && (
              <p className="font-body text-[14px] text-[hsl(var(--foreground))]">
                <span className="font-mono text-[11px] text-[hsl(var(--foreground-muted))] uppercase tracking-[0.18em] mr-2">Out</span>
                {arrival.airline || trip.arrival_airline} {arrFlight}
              </p>
            )}
            {depFlight && (
              <p className="font-body text-[14px] text-[hsl(var(--foreground))]">
                <span className="font-mono text-[11px] text-[hsl(var(--foreground-muted))] uppercase tracking-[0.18em] mr-2">Home</span>
                {dep.airline || trip.departure_airline} {depFlight}
              </p>
            )}
          </div>
        ) : (
          <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            Flights will appear here once ticketed.
          </p>
        )}
      </Surface>
    </div>
  );
}

// --- Highlights -----------------------------------------------------
function Highlights({ highlights }) {
  if (!Array.isArray(highlights) || highlights.length === 0) return null;
  return (
    <motion.div variants={fadeInUp}>
      <EditorialRule label="What to expect" />
      <ul className="mt-6 grid sm:grid-cols-2 gap-x-10 gap-y-4 list-none" data-testid="trip-highlights">
        {highlights.map((h, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="mt-2 size-1 rounded-full bg-[hsl(var(--accent-gold))] shrink-0" aria-hidden />
            <p className="font-accent italic text-[16px] leading-relaxed text-[hsl(var(--foreground))]">
              {typeof h === "string" ? h : h.title || h.name}
            </p>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

// --- Trust signals: agreements + onboarding visible to the client ---
function TrustSignals({ tripId }) {
  const [agreements, setAgreements] = React.useState(null);
  const [forms, setForms] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    Promise.allSettled([
      api.get("/client-portal/agreements"),
      api.get("/client-portal/forms"),
    ]).then(([ra, rf]) => {
      if (!alive) return;
      const aList = ra.status === "fulfilled" && Array.isArray(ra.value.data) ? ra.value.data : [];
      const fList = rf.status === "fulfilled" && Array.isArray(rf.value.data) ? rf.value.data : [];
      // Filter agreements to this trip OR client-wide (no trip_id)
      setAgreements(aList.filter((a) => !a.trip_id || a.trip_id === tripId));
      setForms(fList);
    });
    return () => {
      alive = false;
    };
  }, [tripId]);

  if (agreements === null && forms === null) return null;
  const acked = (agreements || []).filter((a) => a.status === "acknowledged" || a.status === "signed");
  const pendingAck = (agreements || []).filter((a) => a.status === "sent");
  const activePacket = (forms || []).find((p) => p.status === "in_progress" || p.status === "sent");
  const completedPacket = (forms || []).find((p) => p.status === "completed");

  if (acked.length === 0 && pendingAck.length === 0 && !activePacket && !completedPacket) {
    return null;
  }

  return (
    <motion.div variants={fadeInUp} data-testid="client-trust-signals">
      <EditorialRule label="On the record" />
      <div className="mt-6 grid sm:grid-cols-2 gap-4 sm:gap-5">
        {(acked.length > 0 || pendingAck.length > 0) && (
          <Surface variant="default" padded className="!p-5" data-testid="client-agreements-card">
            <div className="flex items-center gap-2 mb-3">
              <ScrollText className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              <Heading level="eyebrow">Agreements</Heading>
            </div>
            {acked.length > 0 && (
              <div className="space-y-1.5">
                {acked.slice(0, 4).map((a) => (
                  <div key={a.id} className="flex items-start gap-2" data-testid={`client-agreement-${a.id}`}>
                    <CheckCircle2 className="size-3.5 mt-0.5 text-[hsl(var(--accent-sage,170_18%_42%))] shrink-0" strokeWidth={1.5} />
                    <div className="min-w-0 flex-1">
                      <p className="font-body text-[13.5px] truncate">{a.title || a.type}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                        Acknowledged {a.acknowledged_at ? fmt.formatDate(a.acknowledged_at) : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {pendingAck.length > 0 && (
              <div className={`${acked.length > 0 ? "mt-3 pt-3 border-t border-[rgba(0,0,0,0.05)]" : ""} space-y-1.5`}>
                {pendingAck.slice(0, 3).map((a) => (
                  <div key={a.id} className="flex items-start gap-2">
                    <span className="size-3.5 mt-0.5 rounded-full border border-[hsl(var(--accent-gold))] shrink-0" />
                    <p className="font-body text-[13.5px] truncate">{a.title || a.type}<span className="ml-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]">awaiting you</span></p>
                  </div>
                ))}
              </div>
            )}
          </Surface>
        )}
        {(activePacket || completedPacket) && (
          <Surface variant="default" padded className="!p-5" data-testid="client-onboarding-card">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              <Heading level="eyebrow">Onboarding</Heading>
            </div>
            {completedPacket && (
              <div className="flex items-start gap-2">
                <CheckCircle2 className="size-3.5 mt-0.5 text-[hsl(var(--accent-sage,170_18%_42%))] shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-body text-[13.5px]">{completedPacket.section_slugs?.length || 0} sections complete</p>
                  <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
                    Thank you — your advisor has everything.
                  </p>
                </div>
              </div>
            )}
            {activePacket && !completedPacket && (
              <div>
                <p className="font-body text-[13.5px]">
                  {activePacket.completed_section_slugs?.length || 0}/{activePacket.section_slugs?.length || 0} sections done
                </p>
                <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] mt-1">
                  Pick up where you left off via the link your advisor sent.
                </p>
              </div>
            )}
          </Surface>
        )}
      </div>
    </motion.div>
  );
}

// --- Page ---------------------------------------------------------
export default function ClientTripHome() {
  const { tripId } = useParams();
  const { isPreview, base } = usePreviewBase();
  // In preview, the inner client paths must stay under the preview wrapper
  // so the axios interceptor keeps rewriting requests. Without this we land
  // on /portal/client/... which has no advisor session and 401s.
  const itineraryHref = isPreview
    ? `${base}/itinerary`
    : `/portal/client/trip/${tripId}/itinerary`;
  const conciergeHref = isPreview
    ? `${base}` // preview doesn't yet route concierge — fall back to home
    : "/portal/client/concierge";
  const [trip, setTrip] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/client-portal/trips/${tripId}`)
      .then(({ data }) => alive && setTrip(data))
      .catch((e) => {
        if (!alive) return;
        const status = e?.response?.status;
        setError(
          status === 404 || status === 403
            ? "Your experience is being curated."
            : "We couldn't open this experience.",
        );
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [tripId]);

  if (loading) {
    return (
      <div className="space-y-8">
        <Skeleton className="aspect-[21/9] w-full" />
        <div className="grid sm:grid-cols-3 gap-5">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <Empty
        title={error}
        icon={<MapPin className="size-7" strokeWidth={1.25} />}
        action={
          <PortalButton as={Link} to="/portal/client/experiences" variant="ghost" size="sm">
            Back to your experiences
          </PortalButton>
        }
      />
    );
  }
  if (!trip) return null;

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10">
      <motion.div variants={fadeInUp}>
        <PortalBreadcrumb
          parent={{ to: "/portal/client/experiences", label: "Your experiences" }}
          trail={[{ label: trip.client_trip_title || trip.destinations || "Experience" }]}
          exitTo="/"
          exitLabel="Exit to website"
        />
      </motion.div>

      <TripHero trip={trip} />

      <ClientConcierge trip={trip} />

      <motion.div variants={fadeInUp}>
        <EditorialRule label="At a glance" />
        <div className="mt-6">
          <Logistics trip={trip} />
        </div>
      </motion.div>

      <Highlights highlights={trip.key_highlights} />

      {/* Wave 26.6 — Destinations + weather (advisor-controlled visibility) */}
      <motion.div variants={fadeInUp}>
        <ClientDestinationsView tripId={tripId} />
      </motion.div>

      <TrustSignals tripId={tripId} />

      {/* Wave 26.15 — Pre-trip readiness card (only shows when revealed) */}
      <ReadinessTeaser tripId={tripId} />

      <motion.div variants={fadeInUp}>
        <EditorialRule label="The deeper plan" />
        <div className="mt-6 grid sm:grid-cols-2 gap-4 sm:gap-5">
          <Surface variant="default" padded className="!p-5">
            <Heading level="h3">Itinerary</Heading>
            <p className="mt-2 font-accent italic text-[14px] text-[hsl(var(--foreground-secondary))]">
              Day-by-day arrangements, restaurants, and quiet moments.
            </p>
            <div className="mt-4">
              <PortalButton
                as={Link}
                to={itineraryHref}
                variant="ghost"
                size="sm"
                data-testid="open-itinerary"
              >
                Open itinerary
              </PortalButton>
            </div>
          </Surface>
          <Surface variant="default" padded className="!p-5">
            <Heading level="h3">Concierge</Heading>
            <p className="mt-2 font-accent italic text-[14px] text-[hsl(var(--foreground-secondary))]">
              Anything on your mind? Your advisor reads every note.
            </p>
            <div className="mt-4">
              <PortalButton
                as={Link}
                to={conciergeHref}
                variant="ghost"
                size="sm"
                icon={<MessageCircle className="size-3.5" strokeWidth={1.5} />}
                data-testid="open-messages"
              >
                Open Concierge
              </PortalButton>
            </div>
          </Surface>
        </div>
      </motion.div>
    </motion.div>
  );
}
